import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';

import clsx from 'clsx';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';

import { ReactComponent as Box } from '@quick/assets/icons/form/box.svg';
import { ReactComponent as Trash } from '@quick/assets/icons/trash.svg';
import { Button } from '@quick/components/Button';
import { WrappedInput, WrappedSelect } from '@quick/components/Forms';
import { Switch } from '@quick/components/Switch';
import type { UnitTypesRes } from '@quick/go-api-types/money/unit-types';
import type { IServiceType } from '@quick/go-api-types/services/country/lines/[id]';

import { useAppTranslation } from '@/app/i18n/client';
import { ReactComponent as Plus2 } from '@/assets/plus2.svg';
import { Separator } from '@/components/core';
import { useCanReadWrite } from '@/layers/core/deal/services/useCanReadWrite';
import {
  limitToMinValue,
  useCurrencyUtils,
} from '@/layers/core/deal/services/useCurrencyUtils';
import { useFetch } from '@/layers/core/deal/services/useFetch';
import { useModulePermissions } from '@/layers/core/deal/services/useModulePermissions';

import CalculateFromPackage from './CalculateFromPackage';
import {
  animationClassName,
  focusWithinClassName,
  gapClassName,
  groupClassName,
} from './classNames';
import { DEFAULT_UNITS_BY_MAGNITUDE, TEXT_FIELD_ID, UNITS } from './constants';
import { useFormDataContext } from './FormDataContext';
import { CommonTitle } from './Titles';
import type { IForm } from './types';
import { sortAdditionalFields, stripNumericalUnitSymbols } from './utils';

export function Packages() {
  const { t } = useAppTranslation(['common', 'demand']);
  const { control } = useFormContext<IForm>();
  const { serviceType } = useFormDataContext();
  const id = serviceType?.id;

  const additionalFields = useMemo(
    () =>
      serviceType?.additional_fields
        .filter(
          ({ field: { is_package, is_for_offer } }) =>
            is_package && is_for_offer
        )
        .sort(sortAdditionalFields),
    [serviceType]
  );

  const { fields, replace, remove, append } = useFieldArray({
    control,
    name: 'packages',
  });

  useEffect(() => {
    if (!id) return;
    if (fields.length === 0) replace([{ f: {}, units: 1 }]);
  }, [id, fields.length, replace]);

  const selectId = useId();

  const isQuantiable = serviceType?.multi_load;
  if (!additionalFields?.length) return null;
  return (
    <div
      className={clsx(
        'flex flex-col',
        groupClassName,
        gapClassName,
        animationClassName,
        focusWithinClassName
      )}
    >
      <div className="flex flex-wrap gap-4">
        <CommonTitle title={t('demand:shipmentItems')} Icon={Box} />
        <div className="ml-auto flex flex-wrap justify-end gap-4">
          <Controller
            control={control}
            name="uom"
            render={({ field: { value, ...field } }) => (
              <WrappedSelect
                {...field}
                value={value || 'metric'}
                id={selectId}
                label={t('common:measurementSystem')}
                isOnModal
                className="w-36"
              >
                <option value="metric">
                  {t('common:measurementSystems.metric')}
                </option>
                <option value="us">
                  {t('common:measurementSystems.imperial')}
                </option>
              </WrappedSelect>
            )}
          />
          {isQuantiable && (
            <Button
              className="flex items-center gap-4"
              kind="secondary"
              variant="accept"
              // disabled={!show}
              onClick={() =>
                append({
                  units: 1,
                  f: {},
                })
              }
              type="button"
            >
              <Plus2 className="w-4" />
              {t('common:add')}
            </Button>
          )}
        </div>
      </div>
      {fields.map((v, i) => {
        return (
          <Package
            key={v.id}
            index={i}
            remove={fields.length > 1 ? () => remove(i) : undefined}
            fields={additionalFields}
          />
        );
      })}
      <Separator />
      <TotalPackage fields={additionalFields} packagesNumber={fields.length} />
    </div>
  );
}

function Package({
  index,
  remove,
  fields,
}: {
  index: number;
  remove?: () => void;
  fields: IServiceType['additional_fields'];
}) {
  const { t } = useAppTranslation(['demand']);
  const { serviceType } = useFormDataContext();
  const { register, watch } = useFormContext<IForm>();
  // TODO: Use service flag
  const isQuantiable = serviceType?.multi_load;
  const canRemove = remove && isQuantiable;

  const uom = watch('uom') || 'metric';

  const ref = useRef(new Map<string, () => void>());

  useEffect(() => {
    ref.current.forEach((v) => v());
    // use size as a trigger because we want to calculate after the form is filled from quote
    // and we don't have a good way to know when the form is filled from here
  }, [uom, fields, ref.current.size]);

  return (
    <div
      className={clsx(
        'group grid grid-cols-[auto,1fr] items-center gap-4 self-stretch',
        canRemove && 'sm:grid-cols-[auto,1fr,auto]'
      )}
    >
      <CalculateFromPackage index={index} />
      <span
        className="group-focus-within:bg-brand flex h-9 w-9 shrink-0 select-none items-center
          justify-center rounded-full border border-gray-100 text-xl font-semibold
          text-gray-400 group-focus-within:border-0 group-focus-within:text-gray-800
          dark:border-gray-600 dark:bg-gray-700 dark:text-white"
      >
        {index + 1}
      </span>
      <div
        className={clsx(
          'flex min-w-0 flex-col gap-x-4 gap-y-2',
          canRemove && 'row-span-2 sm:row-span-1'
        )}
      >
        <div className="grid min-w-0 grid-cols-[repeat(auto-fit,minmax(8rem,1fr))] gap-x-4 gap-y-2">
          {isQuantiable && (
            <WrappedInput
              isOnModal
              type="number"
              wrappedClassName="h-[42px]"
              innerClassName="no-arrows"
              className=""
              label={t('demand:quantity')}
              min={1}
              {...register(`packages.${index}.units`, {
                valueAsNumber: true,
                onChange: () => {
                  ref.current.forEach((v) => v());
                },
              })}
            />
          )}
          {fields.map((field) => (
            <Field
              field={field}
              index={index}
              key={field.field.id}
              ref={(r) => {
                if (r) ref.current.set(field.field.id.toString(), r);
              }}
            />
          ))}
        </div>
      </div>
      {canRemove && (
        <button
          className="group/rm aspect-square text-red-600"
          onClick={remove}
          type="button"
        >
          <Trash className="h-8 w-8 group-hover/rm:fill-red-300 group-focus/rm:fill-red-300" />
        </button>
      )}
    </div>
  );
}

const Field = forwardRef<
  () => void,
  {
    index: number;
    field: IServiceType['additional_fields'][number];
  }
>(function Field({ index, field }, ref) {
  const { t } = useAppTranslation(['common', 'demand']);
  const { control, setValue, getValues, register, watch } =
    useFormContext<IForm>();
  const { country_id } = useFormDataContext();

  const permissions = useModulePermissions();
  const { w: canEditDensityClass } = useCanReadWrite(
    permissions?.operations?.services?.densityClass
  );

  const stops = watch('stops');
  const uom = watch('uom') || 'metric';

  const magnitude = field.field.magnitude?.id;
  const id = field.field.id;
  const packagePrefix = `packages.${index}` as const;
  const fieldPrefix =
    `${packagePrefix}.f.${id}_${field.field.field_type.id === 4 ? TEXT_FIELD_ID : ''}` as const;
  const { data } = useFetch<UnitTypesRes>(
    !!magnitude && {
      url: '/money/unit-types',
      params: { magnitude, pagination: false },
    },
    { revalidateOnFocus: false }
  );

  const factors = useMemo(() => {
    return Object.values(data ?? {})?.reduce(
      (p, v) => {
        p[v.id] = Number(
          uom === 'metric' ? v.conversion_factor : v.conversion_factor_us
        );
        return p;
      },
      {} as Record<string, number>
    );
  }, [data, uom]);

  const calculateTotal = useCallback(() => {
    const [field, units] = getValues([
      fieldPrefix,
      `${packagePrefix}.units`,
    ]) as [
      { unit_type: number; dimensions: Record<string, string> },
      number | undefined,
    ];
    if (typeof field !== 'object') return;
    const u = units ?? 1;
    const values = Object.values(field.dimensions ?? {});
    const product = values.reduce((p, v) => p * Number(v) || 0, 1);
    const factor = factors[field.unit_type] ?? 1;
    const subtotal = product * factor || 0;
    const total = u * subtotal || 0; // Avoid NaN

    // console.log({
    //   values,
    //   c: values.map((v) => Number(v)),
    //   u,
    //   product,
    //   factor,
    //   total,
    // });
    setValue(
      `${fieldPrefix}.sub_total`,
      // TODO: Narrow types to avoid casting
      subtotal as never
    );
    setValue(
      `${fieldPrefix}.total`,
      // TODO: Narrow types to avoid casting
      total as never
    );
  }, [factors, fieldPrefix, packagePrefix, setValue, getValues]);

  useImperativeHandle(ref, () => calculateTotal, [calculateTotal]);

  const defaultUnit = data?.find(({ default: def, id }) => {
    if (uom === 'metric') return def;
    const defaults =
      DEFAULT_UNITS_BY_MAGNITUDE[
        magnitude as keyof typeof DEFAULT_UNITS_BY_MAGNITUDE
      ];
    if (!defaults) return def;
    return defaults.us === id;
  });

  const initialUnit =
    (country_id === 4 &&
      data?.find(({ unit_symbol }) => /lb|in/.test(unit_symbol))) ||
    defaultUnit;

  useEffect(() => {
    if (!initialUnit) return;
    const fieldName = `${fieldPrefix}.unit_type` as const;
    const unit = getValues(fieldName);
    if (!unit)
      // TODO: Narrow field type
      setValue(fieldName, initialUnit.id as never, { shouldDirty: true });
  }, [initialUnit, fieldPrefix, setValue, getValues]);

  const { formatValue, cleanValue, limitToMaxValue, locale } = useCurrencyUtils(
    undefined,
    {
      maximumFractionDigits: 3,
    }
  );

  // Field type numeric
  if (field.field.field_type.id === 1)
    return (
      <>
        {field.field.magnitude.dimensions.map((d) => (
          <Controller
            key={d}
            name={`${fieldPrefix}.dimensions.${d}`}
            control={control}
            rules={{
              required: field.required && t('common:msg.required_field'),
              validate: (v) =>
                field.required && !Number(v)
                  ? t('common:msg.required_field')
                  : undefined,
            }}
            shouldUnregister
            render={({ field: f, fieldState: { error } }) => (
              <WrappedInput
                {...f}
                isOnModal
                requiredMark={field.required}
                autoComplete="off"
                wrappedClassName="h-[42px]"
                value={f.value ? formatValue(`${f.value}`) : '0'}
                onChange={(e) => {
                  let newVal = limitToMaxValue(
                    cleanValue(e.target.value),
                    9_999_999
                  );
                  newVal = limitToMinValue(newVal, 0);
                  f.onChange(newVal);
                  calculateTotal();
                }}
                label={
                  initialUnit?.unit_symbol === '#'
                    ? `#${field.field.name}`
                    : field.field.name === 'Weight'
                      ? t('demand:per_unit', {
                          unit: t(
                            `demand:units.${field.field.name as 'Weight'}`,
                            {
                              defaultValue: field.field.name,
                            }
                          ),
                        })
                      : `${t(`demand:units.${d as 'Height'}`, {
                          defaultValue: d,
                        })} (${t('demand:per_unit', {
                          unit: t(
                            `demand:units.${field.field.name as 'Height'}`,
                            {
                              defaultValue: field.field.name,
                            }
                          ),
                        })})`
                }
                error={error?.message && ' '}
              />
            )}
          />
        ))}
        <Controller
          name={`${fieldPrefix}.unit_type`}
          control={control}
          rules={{
            validate: (v) => {
              if (field.required && !v) return t('common:msg.required_field');
            },
          }}
          render={({ field: f }) => (
            <WrappedSelect
              {...f}
              isOnModal
              onChange={(e) => {
                f.onChange(e);
                calculateTotal();
              }}
              className={clsx(initialUnit?.unit_symbol === '#' && 'hidden')}
              wrappedClassName="h-[42px]"
            >
              {data?.map(({ id, unit_symbol }) => (
                <option value={id} key={id}>
                  {stripNumericalUnitSymbols(unit_symbol)}
                </option>
              ))}
            </WrappedSelect>
          )}
        />

        <Controller
          name={`${fieldPrefix}.sub_total`}
          control={control}
          shouldUnregister
          defaultValue={0 as unknown as undefined} // Sorry again. Type unions
          render={({ field: f }) => {
            const symbol = defaultUnit?.unit_symbol
              .replace('^3', '³')
              .replace('^2', '²');
            return (
              <WrappedInput
                {...f}
                autoComplete="off"
                isOnModal
                className={clsx(defaultUnit?.unit_symbol === '#' && 'hidden')}
                value={
                  f.value
                    ? Number(`${f.value}`).toLocaleString(locale, {
                        maximumSignificantDigits: 2,
                        maximumFractionDigits: 2,
                        trailingZeroDisplay: 'stripIfInteger',
                        roundingPriority: 'morePrecision',
                      })
                    : '0'
                }
                readOnly
                tabIndex={-1}
                symbol={symbol}
                wrappedClassName="h-[42px]"
                symbolRight
                label={t('demand:per_unit', {
                  unit: t(`demand:units.${field.field.name as 'Height'}`, {
                    defaultValue: field.field.name,
                  }),
                })}
                // error={} In theory, this should not error
              />
            );
          }}
        />
        <Controller
          name={`${fieldPrefix}.total`}
          control={control}
          shouldUnregister
          defaultValue={0 as unknown as undefined} // Sorry again. Type unions
          render={({ field: f }) => {
            const symbol = defaultUnit?.unit_symbol
              .replace('^3', '³')
              .replace('^2', '²');
            return (
              <WrappedInput
                {...f}
                autoComplete="off"
                className="hidden"
                isOnModal
                value={
                  f.value
                    ? Number(`${f.value}`).toLocaleString(locale, {
                        maximumSignificantDigits: 2,
                        maximumFractionDigits: 2,
                        trailingZeroDisplay: 'stripIfInteger',
                        roundingPriority: 'morePrecision',
                      })
                    : '0'
                }
                readOnly
                wrappedClassName="h-[42px]"
                symbol={symbol}
                symbolRight
                label={field.field.name}
                // error={} In theory, this should not error
              />
            );
          }}
        />
      </>
    );
  // Field type boolean
  if (field.field.field_type.id === 2)
    return (
      <Switch
        key={`${field.field.field_filter}_${field.field.name}`}
        {...register(fieldPrefix, {
          shouldUnregister: true,
        })}
        defaultChecked={field.field.boolean_default}
        className={clsx('flex w-full items-center gap-2')}
      >
        <span
          className="flex h-full w-4/5 break-before-auto items-center text-xs font-semibold
            dark:text-white"
        >
          {field.field.name}
        </span>
      </Switch>
    );
  // Field type select
  if (field.field.field_type.id === 3) {
    const isDensityClass = field.field.field_filter === 'density_class';
    const isStackable = field.field.field_filter === 'stackable';
    const validateStopOrder = field.field.field_filter === 'stop_order';

    const disableDensityClass = isDensityClass && !canEditDensityClass;

    return (
      <Controller
        name={fieldPrefix}
        rules={{
          required: field.required && t('common:msg.required_field'),
        }}
        defaultValue={isStackable ? ('1' as never) : undefined}
        control={control}
        render={({ field: f, fieldState: { error } }) => (
          <WrappedSelect
            {...f}
            isOnModal
            value={f.value ?? ''}
            label={field.field.name}
            error={error?.message && ' '}
            requiredMark={field.required}
            wrappedClassName="h-[42px]"
            className={clsx(disableDensityClass && 'pointer-events-none')}
            tabIndex={disableDensityClass ? -1 : undefined}
          >
            <option value="" hidden>
              {isDensityClass ? 'Classifying...' : 'Select'}
            </option>
            {validateStopOrder
              ? stops.map(({ address }, i) => {
                  if (i === 0) return;
                  return (
                    <option value={i} key={i}>
                      {`${t('common:stop')} ${i + 1} - ${address}`}
                    </option>
                  );
                })
              : field.field.goods.types.map((p) => (
                  <option
                    value={p}
                    key={p}
                    hidden={
                      disableDensityClass && p.toString() !== `${f.value}`
                    }
                  >
                    {isStackable && `${p}` === '1' ? 'No' : p}
                  </option>
                ))}
          </WrappedSelect>
        )}
      />
    );
  }
  // Field type text
  if (field.field.field_type.id === 4)
    return (
      <Controller
        name={fieldPrefix}
        rules={{
          required: field.required && t('common:msg.required_field'),
          maxLength: {
            value: 100,
            message: t('common:msg.max_chars_with_var', { max: 100 }),
          },
          validate: (v) => {
            if (
              field.field.name.toLowerCase() === 'nmfc' &&
              v &&
              !/^[0-9]{6}-[0-9]{2}$/.test(v as string)
            )
              return t('common:msg.format', {
                format: 'NNNNNN-NN',
                example: '123456-78',
              });
          },
        }}
        control={control}
        render={({ field: f, fieldState: { error } }) => (
          <WrappedInput
            {...f}
            isOnModal
            readOnly={field.field.field_filter === 'linear_feet'}
            value={f.value ?? ''}
            label={field.field.name}
            error={error?.message}
            requiredMark={field.required}
            wrappedClassName="h-[42px]"
            className=""
          />
        )}
      />
    );
  return null;
});

function TotalPackage({
  fields,
  packagesNumber,
}: {
  fields: IServiceType['additional_fields'];
  packagesNumber: number;
}) {
  const { t } = useAppTranslation(['demand']);
  const numerics = useMemo(
    () => fields.filter(({ field: { field_type } }) => field_type.id === 1),
    [fields]
  );

  const { watch } = useFormContext<IForm>();
  const totals = numerics
    .flatMap(({ field: { id, name, magnitude } }) =>
      Array.from({ length: packagesNumber }).map((_, i) => ({
        name,
        total: watch(`packages.${i}.f.${id}_.total`) as number | undefined,
        magnitude: magnitude.id,
      }))
    )
    .reduce(
      (p, v) => {
        p[v.name] ??= { total: 0, magnitude: v.magnitude };
        p[v.name].total += v.total || 0; // Prevent NaN or undefined
        return p;
      },
      {} as Record<string, { total: number; magnitude: number }>
    );

  let units = 0;
  for (let i = 0; i < packagesNumber; i++)
    units += (watch(`packages.${i}.units`) as number) || 0;

  const sumEntries = Object.entries(totals);

  const volumeTotal = sumEntries.find(([, v]) => v.magnitude === 4)?.[1].total;
  const weightTotal = sumEntries.find(([, v]) => v.magnitude === 2)?.[1].total;

  return (
    <ul
      className="flex flex-col justify-around gap-x-8 gap-y-4 text-gray-800 sm:flex-row
        dark:text-gray-200"
    >
      <li className="flex flex-col items-center">
        <span className="font-semibold">{t('demand:quantity')}:</span>
        {units}
      </li>
      {sumEntries.map(([k, v]) => (
        <FieldSum key={k} title={k} value={v.total} magnitude={v.magnitude} />
      ))}
      {!!volumeTotal && !!weightTotal && (
        <WeightByVolumeUnit volume={volumeTotal} weight={weightTotal} />
      )}
    </ul>
  );
}

type FieldSumProps = {
  title: string;
  value: number;
  magnitude: number;
};

function FieldSum({ title, value, magnitude }: FieldSumProps) {
  const { t } = useAppTranslation(['demand']);
  const { watch } = useFormContext<IForm>();

  const uom = watch('uom') || 'metric';

  const { data } = useFetch<UnitTypesRes>(
    {
      url: `/money/unit-types`,
      params: { magnitude, pagination: false },
    },
    { revalidateOnFocus: false }
  );

  const isVolumeUS = magnitude === 4 && uom === 'us';

  const defaultUnit = data?.find(({ default: def, id }) => {
    if (uom === 'metric') return def;
    // volume
    if (isVolumeUS) return id === UNITS['Cubic Feet'];
    const defaults =
      DEFAULT_UNITS_BY_MAGNITUDE[
        magnitude as keyof typeof DEFAULT_UNITS_BY_MAGNITUDE
      ];
    if (!defaults) return def;
    return defaults.us === id;
  });

  let sumValue = value;
  if (isVolumeUS) {
    sumValue /= Number(defaultUnit?.conversion_factor_us) || 1;
  }

  const { locale } = useCurrencyUtils();

  const unitSymbol = defaultUnit?.unit_symbol;
  return (
    <li className="flex flex-col items-center">
      <span className="font-semibold capitalize">
        {t('demand:total', {
          unit: t(`demand:units.${title as 'Height'}`, { defaultValue: title }),
        })}
        :
      </span>
      {sumValue.toLocaleString(locale, {
        maximumSignificantDigits: 2,
        maximumFractionDigits: 2,
        trailingZeroDisplay: 'stripIfInteger',
        roundingPriority: 'morePrecision',
      })}{' '}
      {unitSymbol?.replace('^3', '³').replace('^2', '²') || ''}
    </li>
  );
}

function WeightByVolumeUnit({
  volume,
  weight,
}: {
  volume: number;
  weight: number;
}) {
  const { t } = useAppTranslation(['demand']);
  const { watch } = useFormContext<IForm>();
  const uom = watch('uom') || 'metric';

  const { locale } = useCurrencyUtils();

  const { data: volumeUnits } = useFetch<UnitTypesRes>(
    {
      url: `/money/unit-types`,
      params: { magnitude: 4 /** Volume */, pagination: false },
    },
    { revalidateOnFocus: false }
  );

  const { data: weightUnits } = useFetch<UnitTypesRes>(
    {
      url: `/money/unit-types`,
      params: { magnitude: 2 /** Weight */, pagination: false },
    },
    { revalidateOnFocus: false }
  );

  const weightUnit = weightUnits?.find(({ default: def, id }) => {
    if (uom === 'metric') return def;
    const defaults = DEFAULT_UNITS_BY_MAGNITUDE[2];
    if (!defaults) return def;
    return defaults.us === id;
  });

  const volumeUnit = volumeUnits?.find(({ default: def, id }) => {
    if (uom === 'metric') return def;
    // show density in cubic feet for US
    return id === UNITS['Cubic Feet'];
  });

  let volumeValue = volume;
  if (uom === 'us' && volumeUnit) {
    volumeValue /= Number(volumeUnit.conversion_factor_us) || 1;
  }

  const weightByVolumeUnit = (weight || 0) / (volumeValue || 1);

  const metricSymbol =
    [weightUnit?.unit_symbol, volumeUnit?.unit_symbol]
      .filter(Boolean)
      .join('/')
      .replace('^3', '³')
      .replace('^2', '²') || '';

  return (
    <li className="flex flex-col items-center">
      <span className="font-semibold">{t('demand:units.Density')}:</span>
      {weightByVolumeUnit.toLocaleString(locale, {
        maximumSignificantDigits: 2,
        maximumFractionDigits: 2,
        trailingZeroDisplay: 'stripIfInteger',
        roundingPriority: 'morePrecision',
      })}{' '}
      {uom === 'us' ? 'PCF' : metricSymbol}
    </li>
  );
}
